/**
 * Kanonischer Rollensatz fuer das Bearbeiten von Wiki-Artikeln
 * ("Aktuelles & Wissen"). Spiegelt die Backend-Security der WikiArticle-
 * Patch-Operation + des WikiAttachmentControllers (#304).
 *
 * Hintergrund #304 (Anja Walter): ein Company-Admin sah den "Bearbeiten"-
 * Button nicht und konnte keine PDF anhaengen, weil mehrere Frontend-Gates
 * nur ROLE_EDITOR/CONTENT_MANAGER/PLATFORM_ADMIN prueften. ROLE_COMPANY_ADMIN
 * fehlte — der JWT-Roles-Claim ist flach (keine Hierarchie-Expansion), also
 * muss die Rolle explizit gelistet sein.
 */
export const WIKI_ARTICLE_EDIT_ROLES = [
    'ROLE_EDITOR',
    'ROLE_TEACHER',
    'ROLE_INSTRUCTOR',
    'ROLE_CONTENT_MANAGER',
    'ROLE_COMPANY_ADMIN',
    'ROLE_PLATFORM_ADMIN',
    'ROLE_OPERATOR',
] as const;

/**
 * Darf der Nutzer (anhand seiner JWT-Rollen) Wiki-Artikel anlegen/bearbeiten?
 */
export const canManageWikiArticles = (permissions?: string[] | null): boolean => {
    if (!Array.isArray(permissions)) {
        return false;
    }
    return WIKI_ARTICLE_EDIT_ROLES.some((role) => permissions.includes(role));
};

/**
 * Rollen, die Wiki-Kategorien loeschen duerfen. Die Delete-Operation von
 * WikiCategory ist im Backend enger gefasst als Post/Patch: ROLE_EDITOR,
 * ROLE_TEACHER und ROLE_INSTRUCTOR erben ueber die role_hierarchy der API
 * kein ROLE_CONTENT_MANAGER und laufen daher in einen 403.
 */
export const WIKI_CATEGORY_DELETE_ROLES = [
    'ROLE_CONTENT_MANAGER',
    'ROLE_COMPANY_ADMIN',
    'ROLE_PLATFORM_ADMIN',
    'ROLE_OPERATOR',
] as const;

/**
 * Darf der Nutzer (anhand seiner JWT-Rollen) Wiki-Kategorien loeschen?
 */
export const canDeleteWikiCategories = (permissions?: string[] | null): boolean => {
    if (!Array.isArray(permissions)) {
        return false;
    }
    return WIKI_CATEGORY_DELETE_ROLES.some((role) => permissions.includes(role));
};

/**
 * Rollen, die fremde Wiki-Kommentare moderieren (loeschen) duerfen (#400).
 *
 * Spiegelt die Backend-Security der WikiComment-Delete/Patch-Operation:
 * `object.getCreatedBy() == user.getId() or is_granted('ROLE_CONTENT_MANAGER')
 * or is_granted('ROLE_COMPANY_ADMIN')`. ROLE_PLATFORM_ADMIN und ROLE_OPERATOR
 * erben ROLE_COMPANY_ADMIN ueber die role_hierarchy der API, muessen hier aber
 * explizit stehen — der JWT-Roles-Claim ist flach (keine Hierarchie-Expansion,
 * vgl. #304/#403).
 */
export const WIKI_COMMENT_MODERATE_ROLES = [
    'ROLE_CONTENT_MANAGER',
    'ROLE_COMPANY_ADMIN',
    'ROLE_PLATFORM_ADMIN',
    'ROLE_OPERATOR',
] as const;

/**
 * Darf der Nutzer (anhand seiner JWT-Rollen) fremde Wiki-Kommentare loeschen?
 */
export const canModerateWikiComments = (permissions?: string[] | null): boolean => {
    if (!Array.isArray(permissions)) {
        return false;
    }
    return WIKI_COMMENT_MODERATE_ROLES.some((role) => permissions.includes(role));
};
